import { EntityIcon } from '@common/Entity/EntityIcons';
import { Entity } from '@common/types/Entity';
import { getIDEUrl, Private_Routes } from '@common/url';
import { renderColumnWithEllipsis } from '@common/utils/renderUtils';
import { useConfigsData } from '@pages/Transpiler/useConfigsData';
import { EditBIcon } from '@prophecy/icons';
import { ExtractUrlPathVariables } from '@prophecy/interfaces/generic';
import { theme } from '@prophecy/theme';
import { ColumnsType, Stack, Table, TableActions, TableIconButton } from '@prophecy/ui-v3';
import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { ConfigurationInfoType } from '../../../../data/apis/transpileTypes';
import { Error } from '../../Import/utils';
import { getStatusColumn, hasStatusWithError, InfoTableHeader, StyledNameContainer } from '../util';

export const ConfigsTable = ({ isBulk, id }: { isBulk: boolean; id: string }) => {
  const { data, isError, isLoading } = useConfigsData(id, isBulk);
  const params = useParams<ExtractUrlPathVariables<typeof Private_Routes.TranspilerDetail.url>>();
  const configColumns: ColumnsType<ConfigurationInfoType> = [
    {
      key: 'name',
      dataIndex: 'name',
      title: 'Name',
      filter: {
        type: 'search',
        searchInputProps: {
          placeholder: 'Search..',
          'data-test-id': 'configs-table-name-search-input'
        }
      },
      render: (name) => {
        return (
          <Stack direction='horizontal' gap={theme.spaces.x4} width='100%' alignY='center'>
            <EntityIcon entity={'TranspilerConfig'} size='xs' />
            <StyledNameContainer grow='1'>{renderColumnWithEllipsis(name)}</StyledNameContainer>
          </Stack>
        );
      }
    },
    {
      key: 'pipelineName',
      dataIndex: 'pipelineName',
      title: 'Pipeline',
      sortable: true,
      render: (name) => {
        return (
          <Stack direction='horizontal' gap={theme.spaces.x4} width='100%' alignY='center'>
            <EntityIcon entity={Entity.Pipeline} size='xs' />
            <StyledNameContainer grow='1'>{renderColumnWithEllipsis(name)}</StyledNameContainer>
          </Stack>
        );
      }
    },
    {
      key: 'configParams',
      dataIndex: 'configParams',
      title: 'Options',
      render: (params: { psets: string[] }) => {
        const psets = (params?.psets || []).join(', ') || '';
        return renderColumnWithEllipsis(psets);
      }
    },
    getStatusColumn(),
    {
      align: 'center',
      dataIndex: 'actions',
      key: 'actions',
      width: '60px',
      resizable: false,
      render: (_: string, rowData) => {
        if (hasStatusWithError(rowData.status)) {
          return null;
        }
        return (
          <TableActions gap={theme.spaces.x12} alignY='center'>
            {rowData.id && (
              <TableIconButton
                icon={<EditBIcon type='default' />}
                elementType='link'
                to={getIDEUrl(
                  {
                    uid: rowData.id,
                    entity: Entity.Pipeline
                  },
                  {
                    configInstance: rowData.name,
                    transpilerSource: params.type,
                    transpilerVersion: params.version,
                    transpilerId: id
                  }
                )}
                data-test-id='configs-table-edit-button'
                aria-label={`Edit ${rowData.name}`}
              />
            )}
          </TableActions>
        );
      }
    }
  ];
  const configs = useMemo(() => data?.data?.configurations || [], [data?.data]);

  if (isError) {
    return <Error message='Some error occurred' />;
  }

  return (
    <Stack width='100%' height='100%' gap={theme.spaces.x12}>
      <InfoTableHeader title='Configurations' count={configs.length} />
      <Stack grow='1'>
        <Table
          fixedHeader={true}
          height='100%'
          loading={isLoading}
          width='100%'
          virtualize={true}
          columns={configColumns}
          rowKey={'rowKey'}
          data-test-id='imported-info-configs-table'
          dataSource={configs.map((config, i) => ({ ...config, rowKey: i }))}
        />
      </Stack>
    </Stack>
  );
};
